import { call, put, select, all } from 'redux-saga/effects';
import * as actions from './actions';
import axios from 'axios';
import get from 'lodash.get';
import { sagas as settingsSaga } from 'store/settings';
import { waitFor } from 'store/helpers';
import { getMetaDataPathInfo } from 'store/event/helpers';
import { campaignOccurrenceTypes } from 'views/Campaign/constants';

const apiRoot = `${window._env_?.REACT_APP_API_PATH}`;
const apiSchemaRoot = `${window._env_?.REACT_APP_SCHEMA_API_PATH}`;

export const getSupportedEventTypesAsync = async (
  tenantId,
  businessContextId
) => {
  return axios
    .get(
      `${apiSchemaRoot}eventType/tenants/${tenantId}/context/${businessContextId}/supportedEventTypes`
    )
    .then(response => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });
};

export const getCorrectEventMetaData = async (path, isStaticType, currentTemplateLanguage) => {
  const [tenantId, , campaignOccurrenceType] = path;

  if (
    campaignOccurrenceType === campaignOccurrenceTypes.SCHEDULED ||
    campaignOccurrenceType === campaignOccurrenceTypes.ON_DEMAND
  ) {
    return getAllAttributesAsync(path, currentTemplateLanguage);
  } else if (isStaticType) {
    return getPMSAttributesAsync(tenantId);
  } else {
    return getEventMetaDataAsync(path, currentTemplateLanguage);
  }
};

export const getEventMetaDataAsync = async (path, currentTemplateLanguage) => {
  const [
    tenantId,
    businessContextId,
    campaignOccurrenceType,
    productCategory,
    productName,
    eventTypeCategory,
    eventType,
    workflow
  ] = path;

  return axios
    .get(
      `${apiSchemaRoot}tenants/${tenantId}/context/${businessContextId}/schema/configuration/localized-attributes?productName=${productName}&eventTypeCategory=${eventTypeCategory}&workflow=${workflow}&eventType=${eventType}&languageCode=${currentTemplateLanguage}`
    )
    .then(response => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });
};

export const getAllAttributesAsync = (path, currentTemplateLanguage) => {
  const [
    tenantId,
    businessContextId,
    campaignOccurrenceType,
    productCategory,
    productName,
    eventTypeCategory,
    ,
    workflow
  ] = path;

  return axios
    .get(
      `${apiSchemaRoot}tenants/${tenantId}/context/${businessContextId}/schema/configuration/localized-attributes?productName=${productName}&eventTypeCategory=${eventTypeCategory}&workflow=${workflow}&languageCode=${currentTemplateLanguage}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export const getPMSAttributesAsync = tenantId => {
  return axios
    .get(
      `${apiRoot}tenants/${tenantId}/event-metadata/pms/attributes?showAllAttributes=false&workflow=campaign`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const supportedEventTypesStore = store => store.events.supportedEventTypes.data;
const supportedEventTypesOptionsStore = store =>
  store.events.supportedEventTypes.options;
const settingsMetaDataStore = store => store.settings.settingsMetaData.data;
const eventMetaDataStore = store => store.events.eventMetaData.data;
const supportedScheduledTypesStore = store =>
  store.events.supportedScheduledTypes;
const supportedOnDemandTypesStore = store =>
  store.events.supportedOnDemandTypes;
const supportedInternalSurveyResponseTypesStore = store =>
  store.events.supportedInternalSurveyResponseTypes;
const eventOptionsStore = store =>
  store.events.supportedEventTypes.allEventOptions;
const currentTemplateLanguageStore = store =>
  store.settings.currentTemplateLanguage;

export function* waitForGetSupportedEventTypes() {
  return yield waitFor(
    actions.getSupportedEventTypes(),
    actions.GETTING_SUPPORTED_EVENT_TYPES_SUCCEEDED,
    actions.GETTING_SUPPORTED_EVENT_TYPES_FAILED
  );
}

export function* waitForGetEventMetaData(data) {
  return yield waitFor(
    actions.getEventMetaData(data),
    actions.GETTING_EVENT_META_DATA_SUCCEEDED,
    actions.GETTING_EVENT_META_DATA_FAILED
  );
}

export function* getEventMetaDataIfNeeded(action) {
  const {
    schemaIdentifier,
    eventTypeCategory,
    workflow,
    campaignOccurrenceType,
    productName,
    productType,
    languageCode
  } = action.payload;

  try {
    yield put(actions.gettingEventMetaData({}));

    yield call(waitForGetSupportedEventTypes);

    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let allEventMetaData = yield select(eventMetaDataStore);
    let supportedEventTypesOptions = yield select(
      supportedEventTypesOptionsStore
    );
    let supportedScheduledTypes = yield select(supportedScheduledTypesStore);
    let supportedOnDemandTypes = yield select(supportedOnDemandTypesStore);
    let supportedInternalSurveyResponseTypes = yield select(
      supportedInternalSurveyResponseTypesStore
    );
    let eventOptions = yield select(eventOptionsStore);
    let currentTemplateLanguage = yield select(currentTemplateLanguageStore);
    let settingsMetaData = yield select(settingsMetaDataStore);
    let { metaDataPath, isStaticType } = getMetaDataPathInfo({
      schemaIdentifier,
      supportedEventTypesOptions,
      supportedScheduledTypes,
      supportedOnDemandTypes,
      supportedInternalSurveyResponseTypes,
      workflow,
      campaignOccurrenceType,
      productName,
      productType,
      eventOptions
    });

    let completeMetaDataPath = [
      tenantId,
      businessContextId,
      campaignOccurrenceType,
      ...metaDataPath
    ];

    let eventMetaData = get(allEventMetaData, completeMetaDataPath);
    if (!eventMetaData && (languageCode || currentTemplateLanguage?.languageCode || settingsMetaData?.languageCode)) {
      eventMetaData = yield call(
        getCorrectEventMetaData,
        completeMetaDataPath,
        isStaticType,
        languageCode || currentTemplateLanguage?.languageCode || settingsMetaData?.languageCode
      );
      eventMetaData.map((data) => {
        if (data.schemaType === 'PREFERENCE_INSIGHT') {
          Object.keys(data.preferenceInsightConditionAttributes).forEach((key) => {
            data.preferenceInsightConditionAttributes[key].forEach((item) => {
              data.schemaConditionAttributes.push({ ...item, conditionAttributeType: "String" });
            });
          });
        } else if (data.schemaType === 'ADDITIONAL_GUEST_DATA_INSIGHT') {
          data.additionalGuestDataInsightConditionAttributes.forEach((item) => {
            data.schemaConditionAttributes.push({ ...item, conditionAttributeType: "String" });
          });
        }
      });
    }
  
    yield put(
      actions.gettingEventMetaDataSucceeded({ eventMetaData, metaDataPath })
    );
  } catch (ex) {
    yield put(actions.gettingEventMetaDataFailed({ error: ex }));
  }
}

export function* getSupportedEventTypesIfNeeded(action, force) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let supportedEventTypes = yield select(supportedEventTypesStore);
    let settingsMetaData = yield select(settingsMetaDataStore);

    yield put(actions.gettingSupportedEventTypes({}));

    ({ supportedEventTypes } = yield all({
      settingsMetaData:
        settingsMetaData &&
        settingsMetaData.businessContextId === businessContextId
          ? settingsMetaData
          : yield call(settingsSaga.waitForGetAllMetaData),
      supportedEventTypes:
        supportedEventTypes &&
        supportedEventTypes.businessContextId === businessContextId
          ? supportedEventTypes
          : yield call(getSupportedEventTypesAsync, tenantId, businessContextId)
    }));

    let mergedMetaData = yield select(settingsMetaDataStore);

    yield put(
      actions.gettingSupportedEventTypesSucceeded({
        supportedEventTypes,
        mergedMetaData
      })
    );
  } catch (ex) {
    yield put(actions.gettingSupportedEventTypesFailed({ error: ex }));
  }
}
